import { useRouter } from 'next/router'
import SEOTitle from '@chegg-wt/core/components/SEOTitle'
import MetaDescription from '@chegg-wt/core/components/Head/MetaDescription'
import SourceSearch, {
  SourceSearchPageStructureTop,
} from '@chegg-wt/core/components/Layouts/SourceSearch'
import { getLvpLayout } from '@chegg-wt/core/components/Layouts/LargeVideoPlayerLayout'
import { NextPageWithLayout } from '@chegg-wt/core/types/layout'
import { NextPageContext } from 'next'
import {
  getActualStyleId,
  getCapitalizedSEONameOfStyle,
  isStyleIdInValid,
} from '@chegg-wt/core/utils/styles'
import {
  getSourceTypeName,
  validateUrlSourceType,
} from '@chegg-wt/core/utils/sourceTypes'
import { Config } from '@chegg-wt/core/config'
import { getUrlStyleNameV2 } from '@chegg-wt/core/api/oneGraph'
import { useOnUnloadTimeOnPage } from '@chegg/time-on-page'
import { getStyleSourceDesc } from '../../copy/seo-content/seoDescriptions'
import { matchSourceSegment } from '../../utils/routes'
import upgradeCopy from '../../copy/upgrade'
import considerCredibility from '../../copy/credibility'

interface SourceSearchPageProps {
  initialUrlStyleName: string
}
const SourceSearchPage: NextPageWithLayout<SourceSearchPageProps> = ({
  initialUrlStyleName,
}) => {
  const router = useRouter()
  const { style, source: citeASource } = router.query as { source: string; style: string }
  let {
    params: { source },
  } = matchSourceSegment(citeASource)

  source = typeof source === 'string' ? source : ''

  const styleContainsIeee = (urlStyle: string) => {
    const parts = urlStyle.split('-')
    // Check if the urlStyle is a subset of ieee style
    return parts.includes('ieee')
  }

  const formattedSource = getSourceTypeName(source)
  const formattedStyle = styleContainsIeee(style)
    ? 'IEEE'
    : getCapitalizedSEONameOfStyle(style)
  useOnUnloadTimeOnPage()
  const newStyleSourceDesc = getStyleSourceDesc(style, formattedSource.toLowerCase())

  return (
    <>
      <SEOTitle
        title={`Free Citing a ${formattedSource} in ${formattedStyle} | Citation Machine`}
      />
      <MetaDescription
        content={
          newStyleSourceDesc === 'Not found'
            ? `Creating accurate citations in ${formattedStyle} has never been easier! Automatically cite a ${formattedSource.toLowerCase()} in ${formattedStyle} by using Citation Machine's free citation generator.`
            : newStyleSourceDesc
        }
      />
      <SourceSearch
        copy={{
          considerCredibility,
          upgradeCopy,
        }}
        styleId={getActualStyleId(style)}
        initialUrlStyleName={initialUrlStyleName}
        initialSourceType={source}
      />
    </>
  )
}

export const getServerSideProps = async ({ query }: NextPageContext) => {
  const { source: citeASource, style } = query as { source: string; style: string }
  const [initialUrlStyleName] = await getUrlStyleNameV2(style)

  const {
    params: { source },
  } = matchSourceSegment(citeASource)
  return {
    props: {
      initialUrlStyleName,
      isDefaultStyle: getActualStyleId(style) === Config.currentStyle,
      currentSourceType: source,
    },
    notFound: !validateUrlSourceType(source, true) || isStyleIdInValid(style),
  }
}

SourceSearchPage.getPageLayout = (page, ssrProps) =>
  getLvpLayout(page, () => <SourceSearchPageStructureTop {...ssrProps} />)

export default SourceSearchPage
